import { theme } from '@Guidelines/Theme/theme';
import { css } from '@emotion/react';
import { Upload } from 'lucide-react';
import * as React from 'react';
import { Button } from '@verclam/ui-kit';

const zoneStyle = (isDraggingOver: boolean) => css`
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 0.75rem;
	text-align: center;
	padding: 1.625rem 1.25rem;
	border-radius: 18px;
	border: 1.5px dashed ${isDraggingOver ? theme.colors.accent : theme.colors.borderStrong};
	background-color: ${isDraggingOver ? theme.colors.accentSurface : 'transparent'};
	transition: border-color ${theme.motion.duration}, background-color ${theme.motion.duration};
`;

const iconStyle = css`
	display: flex;
	align-items: center;
	justify-content: center;
	width: 3rem;
	height: 3rem;
	border-radius: ${theme.radii.medium};
	background-color: ${theme.states.warning.surface};
	color: ${theme.states.warning.solid};
`;

const titleStyle = css`
	margin: 0;
	font-family: ${theme.fonts.body};
	font-size: 0.9375rem;
	font-weight: ${theme.fontWeights.semibold};
	color: ${theme.colors.ink};
`;

const detailStyle = css`
	margin: 0;
	max-width: 23.75rem;
	font-family: ${theme.fonts.body};
	font-size: 0.78125rem;
	line-height: 1.55;
	color: ${theme.colors.inkMuted};
`;

const hiddenInputStyle = css`
	position: absolute;
	width: 1px;
	height: 1px;
	padding: 0;
	margin: -1px;
	overflow: hidden;
	clip: rect(0, 0, 0, 0);
	white-space: nowrap;
	border: 0;
`;

type Props = {
	readonly acceptedExtensions: readonly string[];
	readonly acceptedFormatsSentence: string;
	readonly maxDurationSentence: string;
	readonly onFileSelected: (file: File) => void;
};

/**
 * File picker of the import: a dashed drop zone, draggable, plus a "Choisir un fichier audio" button for
 * a native file dialog — both funnel into the same `onFileSelected`. `accept` and the sentence below
 * are built entirely from `acceptedExtensions`/`acceptedFormatsSentence`/`maxDurationSentence`: never
 * a hard-coded format or limit (`core.md` §… — the policy is the only source of truth here).
 */
const ImportDropZone: React.FC<Props> = ({
	acceptedExtensions,
	acceptedFormatsSentence,
	maxDurationSentence,
	onFileSelected,
}) => {
	const inputId = React.useId();
	const inputRef = React.useRef<HTMLInputElement>(null);
	const [isDraggingOver, setIsDraggingOver] = React.useState(false);

	const acceptAttribute = React.useMemo(() => acceptedExtensions.join(','), [acceptedExtensions]);

	const handleBrowseClick = React.useCallback(() => {
		inputRef.current?.click();
	}, []);

	const handleInputChange = React.useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const file = event.target.files?.[0];

			// Cleared so the same file can be re-selected after a rejection (a native input keeps its
			// previous value otherwise, and `change` never fires again for the identical path).
			event.target.value = '';

			if (file !== undefined) {
				onFileSelected(file);
			}
		},
		[onFileSelected],
	);

	const handleDragOver = React.useCallback((event: React.DragEvent<HTMLDivElement>) => {
		event.preventDefault();
		setIsDraggingOver(true);
	}, []);

	const handleDragLeave = React.useCallback(() => {
		setIsDraggingOver(false);
	}, []);

	const handleDrop = React.useCallback(
		(event: React.DragEvent<HTMLDivElement>) => {
			event.preventDefault();
			setIsDraggingOver(false);

			const file = event.dataTransfer.files[0];

			if (file !== undefined) {
				onFileSelected(file);
			}
		},
		[onFileSelected],
	);

	return (
		// biome-ignore lint/a11y/noStaticElementInteractions: drag-and-drop surface only — file selection stays reachable without a pointer through the "Parcourir" button and the hidden <input type="file"> below.
		<div
			css={zoneStyle(isDraggingOver)}
			onDragOver={handleDragOver}
			onDragLeave={handleDragLeave}
			onDrop={handleDrop}
		>
			<div css={iconStyle}>
				<Upload size={18} aria-hidden="true" />
			</div>
			<p css={titleStyle}>Déposez le fichier ici</p>
			<Button type="button" label="Choisir un fichier audio" onClick={handleBrowseClick} />
			<p css={detailStyle}>
				{acceptedFormatsSentence} · {maxDurationSentence} maximum. Le fichier est transcrit, puis
				ajouté à la préparation. Rien n’est généré.
			</p>
			<label css={hiddenInputStyle} htmlFor={inputId}>
				Choisir un fichier audio à importer
			</label>
			<input
				ref={inputRef}
				id={inputId}
				css={hiddenInputStyle}
				type="file"
				accept={acceptAttribute}
				onChange={handleInputChange}
			/>
		</div>
	);
};

export default ImportDropZone;
